import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Typography, Tag, Space, Button, Avatar, Spin, Empty, List } from 'antd';
import {
  TeamOutlined, StarOutlined, EnvironmentOutlined, ShareAltOutlined,
  ArrowRightOutlined, NotificationOutlined, TrophyOutlined, DollarOutlined,
} from '@ant-design/icons';
import api from '../../services/api';
import { absoluteUploadUrl } from '../../utils/uploadUrl';
import FollowButton from '../../components/community/FollowButton';
import AdBanner from '../../components/AdBanner';

const { Title, Text, Paragraph } = Typography;
const BRAND = '#25D366';
const BRAND_DARK = '#128C7E';
const BRAND_DARKER = '#075E54';
const INK = '#0b1f1a';

const SKILL_LABEL = {
  iniciante: 'Iniciante', recreativo: 'Recreativo',
  intermediario: 'Intermediário', avancado: 'Avançado', semi_pro: 'Semi-pro',
};

function timeAgo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return 'agora';
  if (s < 3600) return `${Math.floor(s / 60)}min`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return d.toLocaleDateString('pt-BR');
}
function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// Perfil publico do time: comunidade.futezap.com.br/time/{slug}
// Header com foto+nome+cidade, Noticias, Tabela de jogos, CTA.
export default function PublicTeamProfile() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/community/teams/by-slug/${slug}`)
      .then(r => setTeam(r.data))
      .catch(err => { if (err?.response?.status === 404) setNotFound(true); })
      .finally(() => setLoading(false));
    document.title = `${slug} · Futezap Comunidade`;
    return () => { document.title = 'Futezap'; };
  }, [slug]);

  useEffect(() => {
    if (!team) return;
    const setMeta = (prop, content) => {
      let m = document.querySelector(`meta[property="${prop}"]`);
      if (!m) { m = document.createElement('meta'); m.setAttribute('property', prop); document.head.appendChild(m); }
      m.setAttribute('content', content);
    };
    setMeta('og:title', `${team.name} · Futezap Comunidade`);
    setMeta('og:description', `${SKILL_LABEL[team.skill_level] || 'Time'} · ${team.city || ''} · ⭐ ${Number(team.average_rating || 0).toFixed(1)}`);
    if (team.logo_url) setMeta('og:image', absoluteUploadUrl(team.logo_url));
    setMeta('og:url', window.location.href);
    setMeta('og:type', 'profile');
  }, [team]);

  if (loading) return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;

  if (notFound || !team) {
    return (
      <div style={{ minHeight: '100vh', background: '#f5f7fa', padding: '60px 20px', textAlign: 'center' }}>
        <Empty description="Time não encontrado" />
        <Button type="primary" style={{ marginTop: 16 }} onClick={() => nav('/')}>Ir pra Comunidade</Button>
      </div>
    );
  }

  const share = async () => {
    const url = window.location.href;
    const text = `Confere o time ${team.name} no Futezap: ${url}`;
    if (navigator.share) {
      try { await navigator.share({ title: team.name, text, url }); } catch { /* ignore */ }
    } else {
      navigator.clipboard.writeText(url);
      alert('Link copiado!');
    }
  };

  return (
    <div style={{ background: '#fff', minHeight: '100vh' }}>
      <AdBanner publicMode screenOverride="/perfil-publico-time" />
      {/* Header verde */}
      <div style={{
        background: `radial-gradient(ellipse at top left, ${BRAND_DARK}, transparent 50%),
                     radial-gradient(ellipse at top right, ${BRAND}, transparent 50%),
                     linear-gradient(180deg, ${BRAND_DARKER} 0%, #041a15 100%)`,
        color: '#fff', padding: '32px 20px 60px', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -80, right: -80, width: 320, height: 320,
                      borderRadius: '50%', background: BRAND, opacity: 0.25, filter: 'blur(80px)' }} />
        <div style={{ position: 'relative', maxWidth: 500, margin: '0 auto' }}>
          <a href="/" style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            marginBottom: 24, textDecoration: 'none', color: '#fff',
          }}>
            <img src="/pwa/icon-192.png" alt="Futezap" width={36} height={36}
              style={{ borderRadius: 10, boxShadow: '0 6px 14px rgba(0,0,0,0.35)' }} />
            <div>
              <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: -0.3 }}>Futezap</div>
              <div style={{ fontSize: 9, opacity: 0.7, letterSpacing: 1.5, fontWeight: 600 }}>COMUNIDADE</div>
            </div>
          </a>

          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <Avatar size={88} src={absoluteUploadUrl(team.logo_url)} icon={<TeamOutlined />}
              style={{ border: '3px solid #fff', boxShadow: '0 8px 24px rgba(0,0,0,0.3)', background: BRAND_DARK }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 24, fontWeight: 900, letterSpacing: -0.5 }}>{team.name}</div>
              <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>
                <EnvironmentOutlined /> {[team.neighborhood, team.city, team.state].filter(Boolean).join(' · ') || 'Sem localização'}
              </div>
              <Space size={4} wrap style={{ marginTop: 6 }}>
                {team.skill_level && <Tag color="green" style={{ margin: 0 }}>{SKILL_LABEL[team.skill_level] || team.skill_level}</Tag>}
                {team.has_futezap_pro && <Tag color="gold" style={{ margin: 0 }}>⚡ Futezap Pro</Tag>}
              </Space>
            </div>
          </div>

          {/* Stats grandes */}
          <div style={{
            marginTop: 24, display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)', gap: 8,
          }}>
            <div style={statStyle}>
              <div style={statVal}>{Number(team.average_rating || 0).toFixed(1)}</div>
              <div style={statLbl}>RATING</div>
            </div>
            <div style={statStyle}>
              <div style={statVal}>{team.followers || 0}</div>
              <div style={statLbl}>SEGUIDORES</div>
            </div>
            <div style={statStyle}>
              <div style={statVal}>{team.matches?.length || 0}</div>
              <div style={statLbl}>JOGOS</div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
            <FollowButton targetType="team" targetId={team.id} size="large"
              style={{ flex: 1, height: 44 }} />
            <Button size="large" icon={<ShareAltOutlined />} onClick={share}
              style={{
                height: 44, borderRadius: 10, fontWeight: 700, padding: '0 16px',
                background: 'rgba(255,255,255,0.15)', color: '#fff',
                border: '1px solid rgba(255,255,255,0.25)', backdropFilter: 'blur(6px)',
              }}>
              Compartilhar
            </Button>
          </div>
        </div>
      </div>

      {/* Conteudo */}
      <div style={{ maxWidth: 500, margin: '-30px auto 0', padding: '0 20px 40px', position: 'relative' }}>
        {team.description && (
          <Card style={{ borderRadius: 16, marginBottom: 12, borderLeft: `4px solid ${BRAND}` }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: BRAND_DARK, letterSpacing: 0.5, marginBottom: 6 }}>
              SOBRE O TIME
            </div>
            <div style={{ fontSize: 14, color: INK, lineHeight: 1.5 }}>{team.description}</div>
          </Card>
        )}

        {(team.guest_price > 0 || team.monthly_price > 0) && (
          <Card style={{ borderRadius: 16, marginBottom: 12, background: '#fffbe6', border: '1px solid #ffe58f' }}>
            <div style={{ fontSize: 12, color: '#8a6d3b', fontWeight: 700, letterSpacing: 0.5 }}>
              <DollarOutlined /> VALORES
            </div>
            {team.guest_price > 0 && (
              <div style={{ marginTop: 4, fontSize: 14 }}>Avulso: <b>R$ {Number(team.guest_price).toFixed(2)}</b></div>
            )}
            {team.monthly_price > 0 && (
              <div style={{ marginTop: 2, fontSize: 14 }}>Mensal: <b>R$ {Number(team.monthly_price).toFixed(2)}</b></div>
            )}
          </Card>
        )}

        {/* Elenco */}
        {team.members?.length > 0 && (
          <Card style={{ borderRadius: 16, marginBottom: 12 }}
            title={<><TeamOutlined /> Elenco ({team.members_count})</>}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              {team.members.map(m => {
                const inner = (
                  <div style={{ textAlign: 'center', width: 74 }}>
                    <Avatar size={54} src={absoluteUploadUrl(m.profile_photo)}
                      style={{ background: BRAND_DARK, border: m.role === 'owner' ? '2px solid #faad14' : 'none' }}>
                      {m.name?.[0]?.toUpperCase()}
                    </Avatar>
                    <div style={{ fontSize: 11, fontWeight: 600, color: INK, marginTop: 4,
                                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {m.name?.split(' ')[0]}
                    </div>
                    {m.role === 'owner' && (
                      <div style={{ fontSize: 9, color: '#faad14', fontWeight: 700 }}>DONO</div>
                    )}
                  </div>
                );
                return m.slug ? (
                  <a key={m.id} href={`/jogador/${m.slug}`} style={{ textDecoration: 'none' }}>{inner}</a>
                ) : <div key={m.id}>{inner}</div>;
              })}
            </div>
          </Card>
        )}

        {/* Noticias do time — mostra o TIME como autor (não o community_player
            que publicou). A pessoa que publica é o dono/organizador, mas do
            ponto de vista do leitor a publicação é do time. */}
        {team.news?.length > 0 && (
          <Card style={{ borderRadius: 16, marginBottom: 12 }}
            title={<><NotificationOutlined /> Notícias</>}>
            <List
              dataSource={team.news}
              renderItem={n => (
                <List.Item style={{ alignItems: 'flex-start' }}>
                  <List.Item.Meta
                    avatar={<Avatar src={absoluteUploadUrl(team.logo_url)} icon={<TeamOutlined />} />}
                    title={<><b>{team.name}</b> <span style={{ color: '#8c8c8c', fontSize: 11, fontWeight: 400 }}>· {timeAgo(n.created_at)}</span></>}
                    description={<div style={{ color: '#333', whiteSpace: 'pre-wrap', fontSize: 14 }}>{n.text}</div>}
                  />
                </List.Item>
              )}
            />
          </Card>
        )}

        {/* Tabela de jogos */}
        {team.matches?.length > 0 && (
          <Card style={{ borderRadius: 16, marginBottom: 12 }}
            title={<><TrophyOutlined /> Últimos jogos</>}>
            <List
              dataSource={team.matches}
              renderItem={m => {
                const hasScore = m.score_home != null && m.score_away != null;
                const won = hasScore && m.score_home > m.score_away;
                const drew = hasScore && m.score_home === m.score_away;
                const badgeColor = !hasScore ? 'default' : won ? 'green' : drew ? 'gold' : 'red';
                const badgeText = !hasScore ? 'sem placar' : won ? 'V' : drew ? 'E' : 'D';
                return (
                  <List.Item>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>
                        {team.name} <span style={{ color: '#999' }}>vs</span> {m.opponent_name || '—'}
                      </div>
                      <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                        {fmtDate(m.match_date)}
                        {m.match_time ? ` · ${String(m.match_time).slice(0, 5)}` : ''}
                      </div>
                      {m.notes && <div style={{ fontSize: 12, color: '#555', marginTop: 4, fontStyle: 'italic' }}>{m.notes}</div>}
                    </div>
                    <div style={{ textAlign: 'right', minWidth: 90 }}>
                      {hasScore && (
                        <div style={{ fontSize: 20, fontWeight: 900, color: INK }}>
                          {m.score_home} <span style={{ color: '#999', fontWeight: 400 }}>x</span> {m.score_away}
                        </div>
                      )}
                      <Tag color={badgeColor} style={{ marginTop: 4, marginRight: 0 }}>{badgeText}</Tag>
                    </div>
                  </List.Item>
                );
              }}
            />
          </Card>
        )}

        {/* CTA — SO pra visitante nao-logado */}
        {!localStorage.getItem('community_token') && !localStorage.getItem('token') && (
          <Card style={{
            borderRadius: 16, marginTop: 16,
            background: `linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%)`,
            border: `2px solid ${BRAND}`,
          }}>
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <Title level={4} style={{ margin: '8px 0 4px', color: INK }}>Também joga bola?</Title>
              <Paragraph style={{ color: '#5a6a63', marginBottom: 16 }}>
                Cria teu perfil no Futezap Comunidade e joga em times que curtem o teu estilo.
              </Paragraph>
              <Button type="primary" size="large" block onClick={() => nav('/player/login')}
                style={{
                  height: 48, borderRadius: 12, background: BRAND, borderColor: BRAND,
                  fontWeight: 800, boxShadow: '0 8px 20px rgba(37, 211, 102, 0.35)',
                }}>
                Entrar / Criar meu perfil <ArrowRightOutlined />
              </Button>
            </div>
          </Card>
        )}

        <div style={{ textAlign: 'center', marginTop: 24, fontSize: 11, color: '#8c8c8c' }}>
          Feito no Brasil 🇧🇷 pra quem joga bola
        </div>
      </div>
    </div>
  );
}

const statStyle = {
  background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(6px)', borderRadius: 12,
  padding: '10px 6px', textAlign: 'center', border: '1px solid rgba(255,255,255,0.15)',
};
const statVal = {
  fontSize: 22, fontWeight: 900,
  background: `linear-gradient(90deg, ${BRAND}, #a3e635)`,
  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
};
const statLbl = { fontSize: 10, opacity: 0.75, marginTop: 2, letterSpacing: 0.5 };
